// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — HMGP Store (Zustand) — V2 server-side filtering
// Manages HMGP projects and subapplications
// Uses existing paProjects table with workType='hmgp' discriminator
// ═══════════════════════════════════════════════════════════════

import { create } from 'zustand';
import { queryRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import type { HMGPProject, HMGPSubapplication } from '../types/pagrants';
import { WORKER_URL } from '../lib/api-switch';

// ── Table / field keys ───────────────────────────────────────
const FP = FIELDS.paProjects;

// KV config key for subapplications (interim storage)
const SUBAPP_CONFIG_KEY = 'hmgp-subapplications';

/** V2 returns reference fields as { record_id, display_value } objects — extract scalar ID */
function extractRef(val: unknown): string {
  if (!val && val !== 0) return '';
  if (typeof val === 'object' && val !== null) {
    const obj = val as Record<string, unknown>;
    const id = obj.record_id ?? obj.id ?? obj.value;
    return id != null ? String(id) : '';
  }
  return String(val);
}

// ── Row mapper for HMGP projects ─────────────────────────────
function mapHMGPProject(row: Record<string, unknown>): HMGPProject {
  const rid = Number(row.record_id) || Number(row.id) || 0;
  return {
    id: rid, Rid: rid,
    organizationId: extractRef(row[FP.organizationId]),
    disasterId: extractRef(row[FP.disasterId]),
    projectNumber: String(row[FP.projectNumber] ?? ''),
    projectTitle: String(row[FP.title] ?? ''),
    projectType: String(row[FP.damageCategory] ?? ''),
    projectAddress: String(row[FP.address] ?? ''),
    projectCity: String(row[FP.city] ?? ''),
    projectCounty: '',
    projectState: String(row[FP.state] ?? ''),
    projectZip: String(row[FP.zip] ?? ''),
    projectLat: String(row[FP.latitude] ?? ''),
    projectLng: String(row[FP.longitude] ?? ''),
    problemDescription: String(row[FP.siteDescription] ?? ''),
    scopeOfWork: String(row[FP.scopeOfWork] ?? ''),
    estimatedCost: String(row[FP.estimatedCost] ?? ''),
    federalSharePct: String(row[FP.federalSharePct] ?? ''),
    federalShareAmount: '',
    applicantShareAmount: '',
    otherFunding: '',
    otherFundingSource: '',
    bcaRequired: '',
    bcaMethodology: '',
    bcaRatio: '',
    pvBenefits: '',
    pvCosts: '',
    usefulLife: '',
    discountRate: '',
    bcaToolVersion: '',
    bcaCompletedDate: '',
    ehpStatus: '',
    ehpCompletedDate: '',
    nepaLevel: '',
    nepaStatus: '',
    section106Required: '',
    section106Status: '',
    numberOfStructures: '',
    structureType: '',
    preDisasterValue: '',
    floodZone: '',
    repetitiveLoss: '',
    severeRepetitiveLoss: '',
    subapplicationId: '',
    subapplicationDate: '',
    statePriorityRank: '',
    femaAppNumber: '',
    femaAwardDate: '',
    awardAmount: '',
    obligatedAmount: String(row[FP.obligatedAmount] ?? ''),
    drawdownAmount: String(row[FP.drawdownAmount] ?? ''),
    performancePeriodStart: '',
    performancePeriodEnd: String(row[FP.completionDeadline] ?? ''),
    quarterlyReportDue: '',
    phase: String(row[FP.phase] ?? ''),
    status: String(row[FP.status] ?? ''),
    assignedSpecialist: '',
    notes: String(row[FP.notes] ?? ''),
    creatorId: '',
    createdDate: '',
    lastModifiedBy: '',
    lastModifiedDate: '',
  };
}

// ── Types ────────────────────────────────────────────────────

interface HMGPState {
  projects: HMGPProject[];
  subapplications: HMGPSubapplication[];
  loading: boolean;
  loadingSubs: boolean;

  fetchProjects: (disasterId?: number) => Promise<void>;
  fetchSubapplications: () => Promise<void>;
  createProject: (data: Partial<HMGPProject>) => Promise<HMGPProject | null>;
  updateProject: (rid: number, data: Partial<HMGPProject>) => Promise<void>;
  deleteProject: (rid: number) => Promise<void>;
  createSubapplication: (data: Partial<HMGPSubapplication>) => Promise<HMGPSubapplication | null>;
  updateSubapplication: (rid: number, data: Partial<HMGPSubapplication>) => Promise<void>;
  deleteSubapplication: (rid: number) => Promise<void>;
}

// ── Store ────────────────────────────────────────────────────

export const useHMGPStore = create<HMGPState>((set, get) => ({
  projects: [],
  subapplications: [],
  loading: false,
  loadingSubs: false,

  // ── Fetch HMGP projects (filter paProjects by workType='hmgp') ──
  fetchProjects: async (disasterId?: number) => {
    set({ loading: true });
    try {
      const filters: Array<{ field: string; operator: 'eq'; value: unknown }> = [
        { field: FP.workType, operator: 'eq', value: 'hmgp' },
      ];
      if (disasterId) {
        filters.push({ field: FP.disasterId, operator: 'eq', value: disasterId });
      }
      const res = await queryRecords<Record<string, unknown>>(TABLES.paProjects, {
        filters,
        limit: 200,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ projects: res.records.map(mapHMGPProject) });
    } catch (e) {
      console.error('[HMGP] fetchProjects error:', e);
    } finally {
      set({ loading: false });
    }
  },

  // ── Fetch subapplications from KV config (interim) ──
  fetchSubapplications: async () => {
    set({ loadingSubs: true });
    try {
      const resp = await fetch(
        `${WORKER_URL}/api/config/${SUBAPP_CONFIG_KEY}`,
        { headers: { Origin: 'https://apps.opengov.com' } }
      );
      if (resp.ok) {
        const data = await resp.json();
        set({ subapplications: Array.isArray(data) ? data : [] });
      } else {
        set({ subapplications: [] });
      }
    } catch (e) {
      console.error('[HMGP] fetchSubapplications error:', e);
      set({ subapplications: [] });
    } finally {
      set({ loadingSubs: false });
    }
  },

  // ── Create HMGP project (in paProjects with workType='hmgp') ──
  createProject: async (data) => {
    try {
      const payload: Record<string, unknown> = {
        [FP.workType]: 'hmgp',
      };
      if (data.projectNumber !== undefined) payload[FP.projectNumber] = data.projectNumber;
      if (data.projectTitle !== undefined) payload[FP.title] = data.projectTitle;
      if (data.projectType !== undefined) payload[FP.damageCategory] = data.projectType;
      if (data.organizationId !== undefined) payload[FP.organizationId] = data.organizationId;
      if (data.disasterId !== undefined) payload[FP.disasterId] = data.disasterId;
      if (data.problemDescription !== undefined) payload[FP.siteDescription] = data.problemDescription;
      if (data.scopeOfWork !== undefined) payload[FP.scopeOfWork] = data.scopeOfWork;
      if (data.estimatedCost !== undefined) payload[FP.estimatedCost] = data.estimatedCost;
      if (data.federalSharePct !== undefined) payload[FP.federalSharePct] = data.federalSharePct;
      if (data.phase !== undefined) payload[FP.phase] = data.phase;
      if (data.status !== undefined) payload[FP.status] = data.status;
      if (data.projectAddress !== undefined) payload[FP.address] = data.projectAddress;
      if (data.projectCity !== undefined) payload[FP.city] = data.projectCity;
      if (data.projectState !== undefined) payload[FP.state] = data.projectState;
      if (data.projectZip !== undefined) payload[FP.zip] = data.projectZip;
      if (data.notes !== undefined) payload[FP.notes] = data.notes;

      const res = await createRecord<Record<string, unknown>>(TABLES.paProjects, payload);
      await get().fetchProjects();
      const rid = (res as any).record_id as number;
      return get().projects.find(p => p.Rid === rid) || null;
    } catch (e) {
      console.error('[HMGP] createProject error:', e);
      return null;
    }
  },

  // ── Update HMGP project ──
  updateProject: async (rid, data) => {
    try {
      const payload: Record<string, unknown> = {};
      if (data.projectTitle !== undefined) payload[FP.title] = data.projectTitle;
      if (data.projectType !== undefined) payload[FP.damageCategory] = data.projectType;
      if (data.problemDescription !== undefined) payload[FP.siteDescription] = data.problemDescription;
      if (data.scopeOfWork !== undefined) payload[FP.scopeOfWork] = data.scopeOfWork;
      if (data.estimatedCost !== undefined) payload[FP.estimatedCost] = data.estimatedCost;
      if (data.federalSharePct !== undefined) payload[FP.federalSharePct] = data.federalSharePct;
      if (data.phase !== undefined) payload[FP.phase] = data.phase;
      if (data.status !== undefined) payload[FP.status] = data.status;
      if (data.notes !== undefined) payload[FP.notes] = data.notes;
      if (data.organizationId !== undefined) payload[FP.organizationId] = data.organizationId;
      if (data.disasterId !== undefined) payload[FP.disasterId] = data.disasterId;
      if (data.projectAddress !== undefined) payload[FP.address] = data.projectAddress;
      if (data.projectCity !== undefined) payload[FP.city] = data.projectCity;
      if (data.projectState !== undefined) payload[FP.state] = data.projectState;
      if (data.projectZip !== undefined) payload[FP.zip] = data.projectZip;

      if (Object.keys(payload).length > 0) {
        await updateRecord(TABLES.paProjects, rid, payload);
      }
      await get().fetchProjects();
    } catch (e) {
      console.error('[HMGP] updateProject error:', e);
    }
  },

  // ── Delete HMGP project ──
  deleteProject: async (rid) => {
    try {
      await deleteRecord(TABLES.paProjects, rid);
      set(s => ({ projects: s.projects.filter(p => p.Rid !== rid) }));
    } catch (e) {
      console.error('[HMGP] deleteProject error:', e);
    }
  },

  // ── Subapplication CRUD (KV config interim) ──
  createSubapplication: async (data) => {
    try {
      const subs = [...get().subapplications];
      const newId = subs.length > 0 ? Math.max(...subs.map(s => s.Rid)) + 1 : 1;
      const now = new Date().toISOString().split('T')[0];
      const newSub: HMGPSubapplication = {
        id: newId, Rid: newId,
        organizationId: data.organizationId || '',
        disasterId: data.disasterId || '',
        subappNumber: data.subappNumber || `HMGP-SA-${String(newId).padStart(4, '0')}`,
        subappTitle: data.subappTitle || '',
        projectType: data.projectType || '',
        problemStatement: data.problemStatement || '',
        proposedActivity: data.proposedActivity || '',
        estimatedCost: data.estimatedCost || '',
        federalRequest: data.federalRequest || '',
        nonFederalMatch: data.nonFederalMatch || '',
        matchSource: data.matchSource || '',
        projectAddress: data.projectAddress || '',
        projectCity: data.projectCity || '',
        projectCounty: data.projectCounty || '',
        projectState: data.projectState || '',
        projectLat: data.projectLat || '',
        projectLng: data.projectLng || '',
        bcaStatus: data.bcaStatus || 'not_started',
        bcaRatio: data.bcaRatio || '',
        statePriorityScore: data.statePriorityScore || '',
        statePriorityRank: data.statePriorityRank || '',
        submittedDate: data.submittedDate || '',
        submittedBy: data.submittedBy || '',
        reviewerName: data.reviewerName || '',
        reviewDate: data.reviewDate || '',
        reviewNotes: data.reviewNotes || '',
        status: data.status || 'draft',
        linkedProjectId: data.linkedProjectId || '',
        notes: data.notes || '',
        creatorId: data.creatorId || '',
        createdDate: now,
      };
      subs.push(newSub);
      await _saveSubapps(subs);
      set({ subapplications: subs });
      return newSub;
    } catch (e) {
      console.error('[HMGP] createSubapplication error:', e);
      return null;
    }
  },

  updateSubapplication: async (rid, data) => {
    try {
      const subs = get().subapplications.map(s =>
        s.Rid === rid ? { ...s, ...data } : s
      );
      await _saveSubapps(subs);
      set({ subapplications: subs });
    } catch (e) {
      console.error('[HMGP] updateSubapplication error:', e);
    }
  },

  deleteSubapplication: async (rid) => {
    try {
      const subs = get().subapplications.filter(s => s.Rid !== rid);
      await _saveSubapps(subs);
      set({ subapplications: subs });
    } catch (e) {
      console.error('[HMGP] deleteSubapplication error:', e);
    }
  },
}));

// ── KV persistence helper ────────────────────────────────────
async function _saveSubapps(subs: HMGPSubapplication[]) {
  await fetch(
    `${WORKER_URL}/api/config/${SUBAPP_CONFIG_KEY}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Origin: 'https://apps.opengov.com' },
      body: JSON.stringify(subs),
    }
  );
}
